import { showErrorMessage } from 'components/Helpers/Toasts';
import { mapCart, mapPromoCode } from 'connectors/cart/Cart';
import { getUserFriendlyErrors } from 'connectors/lib/friendlyErrorMessageParser';
import { mapPayment } from 'connectors/payments/Payment';
import { mapTransport } from 'connectors/transports/Transports';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useCartQueryApi } from 'graphql/generated';
import { ApplicationErrors } from 'helpers/errors/applicationErrors';
import { getSelectedPickupPoint } from 'helpers/pickupPoint/pickupPoint';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { CurrentCartType } from 'types/cart';
import { GtmMessageOriginType } from 'types/gtm';
import { CombinedError, OperationContext } from 'urql';
import { shallow } from 'zustand/shallow';

export const useCurrentCart = (fromCache = true): CurrentCartType => {
    const [isInitiallyLoaded, setInitialLoadedState] = useState(false);
    const { isUserLoggedIn } = useCurrentUserData();
    const { defaultStoreUuid, selectedStoreUuid, cartUuid, authLoading } = usePersistStore(
        (s) => ({
            defaultStoreUuid: s.defaultStoreUuid,
            selectedStoreUuid: s.selectedStoreUuid,
            cartUuid: s.cartUuid,
            authLoading: s.authLoading,
        }),
        shallow,
    );
    const { currencyCode } = useDomainConfig();
    const { t } = useTranslation();

    const [result, refetchCart] = useCartQueryApi({
        variables: {
            cartUuid,
            defaultStoreUuid,
            selectedStoreUuid: selectedStoreUuid ?? defaultStoreUuid,
        },
        pause: (cartUuid === null && !isUserLoggedIn) || authLoading !== 'not-loading',
        requestPolicy: fromCache ? 'cache-first' : 'network-only',
    });

    useEffect(() => {
        if (cartUuid === null && !isUserLoggedIn) {
            setInitialLoadedState(true);

            return;
        }

        if (!result.fetching) {
            setInitialLoadedState(true);
        }
    }, [cartUuid, isUserLoggedIn, result.fetching]);

    return useMemo(() => {
        if (result.data === undefined) {
            return getEmptyCart(isInitiallyLoaded, refetchCart, false, result.fetching);
        }

        if (isInitiallyLoaded !== true) {
            setInitialLoadedState(true);
        }

        if (cartUuid === null && !isUserLoggedIn) {
            return getEmptyCart(isInitiallyLoaded, refetchCart, true);
        }

        if (result.error !== undefined) {
            const cartError = getUserFriendlyErrors(result.error, t);
            if (
                cartError.applicationError?.type === 'cart-unavailable' ||
                cartError.applicationError?.type === 'invalid-token'
            ) {
                if (authLoading !== 'logout-loading') {
                    showErrorMessage(t('You were automatically logged out after inactivity.'));
                }
            } else {
                // EXTEND CART ERRORS HERE
                handleCartError(result.error, t);
            }

            return getEmptyCart(isInitiallyLoaded, refetchCart, true);
        }

        if (result.data.cart === null) {
            // EXTEND EMPTY CART HERE
            return getEmptyCart(isInitiallyLoaded, refetchCart, true);
        }
        // EXTEND CART UPDATE HERE
        const mappedCart = mapCart(result.data.cart, currencyCode);

        const mappedTransport =
            result.data.cart.transport === null ? null : mapTransport(result.data.cart.transport, currencyCode);

        return {
            cart: mappedCart,
            uuid: result.data.cart.uuid,
            transport: mappedTransport,
            pickupPlace: getSelectedPickupPoint(mappedTransport, result.data.cart.selectedPickupPlaceIdentifier),
            payment: result.data.cart.payment === null ? null : mapPayment(result.data.cart.payment, currencyCode),
            paymentGoPayBankSwift: result.data.cart.paymentGoPayBankSwift,
            promoCodes: result.data.cart.promoCodes.map((promoCode) => mapPromoCode(promoCode, currencyCode)),
            loyaltyClubPoints: result.data.cart.loyaltyClubPoints,
            loyaltyClubPointsMaximum: result.data.cart.loyaltyClubPointsMaximum,
            isLoaded: true,
            isFetching: result.fetching,
            isInitiallyLoaded: isInitiallyLoaded,
            modifications: result.data.cart.modifications,
            hasVoucherProduct: result.data.cart.hasVoucherProduct,
            storesWhereAllProductsAreAvailableTogetherUuids: mappedCart.storesWhereAllProductsAreAvailableTogetherUuids,
            refetchCart,
            isWithCart: true,
        };
    }, [result.data, result.error, isInitiallyLoaded, cartUuid, isUserLoggedIn, currencyCode, refetchCart, t]);
};

const getEmptyCart = (
    isInitiallyLoaded: boolean,
    refetchCart: (opts?: Partial<OperationContext> | undefined) => void,
    isLoaded = true,
    isFetching = false,
): CurrentCartType => ({
    cart: null,
    uuid: null,
    transport: null,
    pickupPlace: null,
    payment: null,
    paymentGoPayBankSwift: null,
    promoCodes: [],
    loyaltyClubPoints: null,
    loyaltyClubPointsMaximum: null,
    isLoaded,
    isFetching,
    isInitiallyLoaded,
    modifications: null,
    storesWhereAllProductsAreAvailableTogetherUuids: [],
    refetchCart,
    isWithCart: false,
});

export const handleCartError = (error: CombinedError, t: Translate) => {
    const { userError, applicationError } = getUserFriendlyErrors(error, t);
    const authLoading = usePersistStore.getState().authLoading;

    switch (applicationError?.type) {
        case 'cart-not-found':
            break;
        case 'invalid-token':
            if (authLoading !== 'logout-loading') {
                showErrorMessage(t('You were automatically logged out after inactivity.'));
            }
            break;
        case ApplicationErrors.default:
            showErrorMessage(applicationError.message, GtmMessageOriginType.cart);
            break;
    }

    if (userError?.validation !== undefined) {
        for (const invalidFieldName in userError.validation) {
            showErrorMessage(userError.validation[invalidFieldName].message, GtmMessageOriginType.cart);
        }
    }
};
